import { z } from "zod";

import { ConditionalDisplayManager } from "../utils/conditionalDisplay";
import { convertStringToBoolean } from "../utils/fieldUtils";

interface FormFieldData {
  terminationDate?: string;
  effectiveDate?: string;
  hasOrthodonticCoverage?: string;
  orthoLifetimeMaxBenefit?: string | null;
  orthoAmountUsed?: string | null;
  orthoAmountRemaining?: string | null;
  orthoPercentagePlanPays?: string | null;
  orthoAgeLimit?: string | null;
  orthoManualOrAutomaticPayments?: string;
  orthoPaymentFrequency?: string;
  orthoInitialPaymentPercentage?: string | null;
  hasOrthoPlanDeductible?: string;
  orthoDeductibleRemaining?: string | null;
  hasOrthoWaitingPeriod?: string;
  orthoDateOfWaitingPeriod?: string;
  coordinationOfBenefits?: string;
  notes?: string;
  preAuthorizationRequired?: string;
  groupAcceptsAssignmentOfBenefits?: string;
  callReferenceNumber?: string;
  verificationMethod?: string;
}

export const formFields: readonly {
  name: string;
  type: "text" | "number" | "date" | "radio" | "dropdown";
  required: boolean;
}[] = [
  { name: "hasOrthodonticCoverage", type: "radio", required: false },
  { name: "effectiveDate", type: "date", required: false },
  { name: "terminationDate", type: "date", required: false },
  { name: "orthoLifetimeMaxBenefit", type: "number", required: false },
  {
    name: "orthoInitialPaymentPercentage",
    type: "number",
    required: false,
  },
  { name: "orthoAmountUsed", type: "number", required: false },
  { name: "orthoAmountRemaining", type: "number", required: false },
  { name: "orthoPercentagePlanPays", type: "number", required: false },
  { name: "orthoAgeLimit", type: "number", required: false },
  { name: "orthoManualOrAutomaticPayments", type: "radio", required: false },
  { name: "orthoPaymentFrequency", type: "radio", required: false },
  { name: "hasOrthoPlanDeductible", type: "radio", required: false },
  { name: "orthoDeductibleRemaining", type: "number", required: false },
  { name: "hasOrthoWaitingPeriod", type: "radio", required: false },
  { name: "orthoDateOfWaitingPeriod", type: "date", required: false },
  { name: "coordinationOfBenefits", type: "text", required: false },
  { name: "notes", type: "text", required: false },
  { name: "preAuthorizationRequired", type: "dropdown", required: false },
  {
    name: "groupAcceptsAssignmentOfBenefits",
    type: "dropdown",
    required: false,
  },
  { name: "callReferenceNumber", type: "text", required: false },
  { name: "verificationMethod", type: "dropdown", required: false },
] as const;

function shouldApplyCrossFieldValidation(data: FormFieldData): boolean {
  // Convert to conditional display format using the original approach
  const conditionalDisplayData = {
    termination_date: data.terminationDate
      ? new Date(data.terminationDate)
      : null,
    effective_date: data.effectiveDate ? new Date(data.effectiveDate) : null,
    has_orthodontic_coverage: convertStringToBoolean(
      data.hasOrthodonticCoverage,
    ),
    ortho_lifetime_max_benefit: data.orthoLifetimeMaxBenefit
      ? parseFloat(data.orthoLifetimeMaxBenefit)
      : null,
    ortho_amount_used: data.orthoAmountUsed
      ? parseFloat(data.orthoAmountUsed)
      : null,
    ortho_amount_remaining: data.orthoAmountRemaining
      ? parseFloat(data.orthoAmountRemaining)
      : null,
    ortho_percentage_plan_pays: data.orthoPercentagePlanPays
      ? parseFloat(data.orthoPercentagePlanPays)
      : null,
    ortho_age_limit: data.orthoAgeLimit ? parseFloat(data.orthoAgeLimit) : null,
    ortho_manual_or_automatic_payments: data.orthoManualOrAutomaticPayments,
    ortho_payment_frequency: data.orthoPaymentFrequency,
    ortho_initial_payment_percentage: data.orthoInitialPaymentPercentage
      ? parseFloat(data.orthoInitialPaymentPercentage)
      : null,
    has_ortho_deductible_plan: convertStringToBoolean(
      data.hasOrthoPlanDeductible,
    ),
    ortho_deductible_remaining: data.orthoDeductibleRemaining
      ? parseFloat(data.orthoDeductibleRemaining)
      : null,
    has_ortho_waiting_period: convertStringToBoolean(
      data.hasOrthoWaitingPeriod,
    ),
    ortho_waiting_period_date: data.orthoDateOfWaitingPeriod
      ? new Date(data.orthoDateOfWaitingPeriod)
      : null,
    coordination_of_benefits: data.coordinationOfBenefits,
    notes: data.notes,
    pre_authorization_required: data.preAuthorizationRequired ?? null,
    group_accepts_assignment_of_benefits:
      data.groupAcceptsAssignmentOfBenefits ?? null,
    call_reference_number: data.callReferenceNumber ?? null,
    verification_method: data.verificationMethod ?? null,
  };

  const amountUsedVisibility =
    ConditionalDisplayManager.getFieldVisibilityState(
      "ortho_amount_used",
      conditionalDisplayData,
    );

  const maxBenefitVisibility =
    ConditionalDisplayManager.getFieldVisibilityState(
      "ortho_lifetime_max_benefit",
      conditionalDisplayData,
    );

  return amountUsedVisibility.isEditable || maxBenefitVisibility.isEditable;
}

export const insuranceVerificationFormSchema = z
  .object(
    formFields.reduce((acc: Record<string, z.ZodType>, field) => {
      if (field.type === "text") {
        acc[field.name] = field.required
          ? z
              .string()
              .min(3, `${field.name} must be at least 3 characters long`)
          : z.string().optional();
      } else if (field.type === "number") {
        if (
          ["orthoPercentagePlanPays", "orthoInitialPaymentPercentage"].includes(
            field.name,
          )
        ) {
          acc[field.name] = z.coerce
            .string()
            .nullable()
            .optional()
            .refine(
              (val) => {
                if (val === null || val === undefined || val === "")
                  return true;
                const num = parseFloat(val);
                return !Number.isNaN(num) && num >= 0 && num <= 100;
              },
              { message: `${field.name} must be a number between 0 and 100` },
            );
        } else if (field.name === "orthoAgeLimit") {
          acc[field.name] = z.coerce
            .string()
            .nullable()
            .optional()
            .refine(
              (val) => {
                if (val === null || val === undefined || val === "")
                  return true;
                const num = parseFloat(val);
                return !Number.isNaN(num) && num >= 0 && num <= 99;
              },
              { message: `${field.name} must be a number between 0 and 99` },
            );
        } else {
          acc[field.name] = z.coerce
            .string()
            .nullable()
            .optional()
            .refine(
              (val) => {
                if (val === null || val === undefined || val === "")
                  return true;
                const num = parseFloat(val);
                return !Number.isNaN(num) && num >= 0;
              },
              { message: `${field.name} must be a valid positive number` },
            );
        }
      } else if (field.type === "date") {
        acc[field.name] = field.required
          ? z.coerce
              .date()
              .transform((date) => date.toISOString().split("T")[0])
          : z.coerce
              .date()
              .transform((date) => date.toISOString().split("T")[0])
              .optional();
      } else if (["dropdown", "radio"].includes(field.type)) {
        acc[field.name] = field.required
          ? z.string().min(1, `${field.name} is required`)
          : z.string().optional();
      }
      return acc;
    }, {}),
  )
  .refine(
    (data) => {
      if (!shouldApplyCrossFieldValidation(data)) {
        return true;
      }

      // Amount used should not exceed lifetime max benefit
      if (
        data.orthoAmountUsed !== null &&
        data.orthoAmountUsed !== undefined &&
        data.orthoAmountUsed !== "" &&
        data.orthoLifetimeMaxBenefit !== null &&
        data.orthoLifetimeMaxBenefit !== undefined &&
        data.orthoLifetimeMaxBenefit !== ""
      ) {
        const amountUsed = parseFloat(data.orthoAmountUsed);
        const maxBenefit = parseFloat(data.orthoLifetimeMaxBenefit);
        if (
          !Number.isNaN(amountUsed) &&
          !Number.isNaN(maxBenefit) &&
          amountUsed > 0 &&
          maxBenefit > 0
        ) {
          return amountUsed <= maxBenefit;
        }
      }
      return true;
    },
    {
      message: "Amount used cannot exceed lifetime maximum benefit",
      path: ["orthoAmountUsed"],
    },
  )
  .superRefine((data, ctx) => {
    if (
      data.effectiveDate &&
      data.terminationDate &&
      new Date(data.effectiveDate) >= new Date(data.terminationDate)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Effective date must be before termination date",
        path: ["effectiveDate"],
      });
    }
  });

export type InsuranceVerificationFormFields = z.infer<
  typeof insuranceVerificationFormSchema
>;
